import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { Loader } from "shared/components/Loader";
import { Link } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  DiscountIcon,
  DiscountTag02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { DiscountCodesDisplayTable } from "views/discounts/components/DiscountCodesDisplayTable";
import { useInfiniteDiscounts } from "views/discounts/hooks/useDiscounts";
import { useQuery } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { useParams } from "react-router-dom";
import { useAuthContext } from "providers/Auth";
import { Card } from "shared/components/Card";
import { useEffect, useRef } from "react";

export const DiscountCodesTab = () => {
  const { propertyId } = useParams();
  const {
    type: { adminPrivileges }
  } = useAuthContext();

  const propertyQuery = useQuery({
    queryKey: ["PROPERTIES", propertyId],
    queryFn: () => addressApi.getProperty(propertyId || ""),
    enabled: !!propertyId
  });

  const templateId = propertyQuery.data?.verification_template?.id;

  const discountsQuery = useInfiniteDiscounts({
    active: true,
    restriction_type: "template",
    template_id: templateId,
    enabled: !!templateId
  });

  const discounts =
    discountsQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const observerRef = useRef<HTMLDivElement | null>(null);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    isFetchingRef.current = discountsQuery.isFetchingNextPage;
  }, [discountsQuery.isFetchingNextPage]);

  useEffect(() => {
    if (!observerRef.current || !discountsQuery.hasNextPage) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          discountsQuery.hasNextPage &&
          !isFetchingRef.current
        ) {
          isFetchingRef.current = true;
          discountsQuery.fetchNextPage();
        }
      },
      { threshold: 0.1, rootMargin: "100px" }
    );

    observer.observe(observerRef.current);
    return () => {
      observer.disconnect();
    };
  }, [discountsQuery.fetchNextPage, discountsQuery.hasNextPage]);

  if (discountsQuery.isLoading || propertyQuery.isLoading)
    return (
      <div className="flex justify-center py-8">
        <Loader />
      </div>
    );

  if (!discounts.length)
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-300px)]">
        <div className="rounded-full bg-alpha-20 p-8 mb-6">
          <HugeiconsIcon icon={DiscountIcon} size={40} strokeWidth={1} />
        </div>
        <h1 className="text-base font-bold text-content-500 mb-2">
          Discount codes will appear here
        </h1>
        <p className="text-sm font-medium text-content-300 mb-6">
          Discount codes are configured in the screening settings
        </p>
        <Show if={adminPrivileges}>
          <Link to="/discounts">
            <Button variant={ButtonVariant.PRIMARY} size={ButtonSize.BASE}>
              Configure discounts
            </Button>
          </Link>
        </Show>
      </div>
    );

  return (
    <Card className="rounded-lg bg-content-1000-inverted">
      <div className="flex justify-between items-center p-6">
        <span>
          <h3 className="text-base font-bold text-content-1000 mb-1">
            Available discount codes for property
          </h3>
          <p className="text-sm text-content-300">
            Share discount codes with applicants so they can reduce their
            application fee
          </p>
        </span>
        <Show if={adminPrivileges}>
          <Link to="/discounts">
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              HugeIcon={DiscountTag02Icon}
            >
              Configure discounts
            </Button>
          </Link>
        </Show>
      </div>
      <DiscountCodesDisplayTable
        discounts={discounts}
        showConfigureButton={true}
      />
      <Show if={discountsQuery.hasNextPage}>
        <div ref={observerRef} className="flex justify-center py-4">
          <Loader />
        </div>
      </Show>
    </Card>
  );
};
